import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { actingGigs } from "../../data/actingData";
import type { ActingGig } from "../../data/actingData";

type ViewMode = "gallery" | "list";

function Gallery() {
  const navigate = useNavigate();
  const [view, setView] = useState<ViewMode>("gallery");

  const getVideoId = (url: string): string | null => {
    if (!url) return null;

    const youtubeMatch = url.match(
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/
    );
    if (youtubeMatch) return youtubeMatch[1];

    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `vimeo:${vimeoMatch[1]}`;

    return null;
  };

  const getGigUrl = (gigName: string): string => {
    return gigName.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  };

  const thumbnailFor = (gig: ActingGig): string | null => {
    if (gig.thumbnail) return gig.thumbnail;
    if (gig.youtube) {
      const id = getVideoId(gig.youtube);
      if (id && !id.startsWith("vimeo:")) {
        return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
      }
    }
    return null;
  };

  return (
    <main className="flex-1 w-full px-4 md:px-8 py-12 max-w-7xl mx-auto">
      <div className="mb-12 text-center">
        <h1 className="text-5xl md:text-6xl font-bold pb-4 bg-gradient-to-r from-yellow-400 via-yellow-500 to-amber-500 bg-clip-text text-transparent">
          Acting Portfolio
        </h1>
      </div>

      <div className="mb-12 max-w-4xl mx-auto">
        <div className="bg-gray-900 rounded-xl overflow-hidden shadow-2xl">
          <div className="relative pt-[56.25%]">
            <iframe
              src="https://www.youtube.com/embed/EppZMjoZUAI"
              title="Acting Demo Reel"
              className="absolute inset-0 w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      </div>

      {/* View toggle */}
      <div className="mb-8 flex justify-center">
        <div className="inline-flex bg-gray-800/50 rounded-lg p-1 backdrop-blur-sm" role="group" aria-label="View mode">
          <button
            type="button"
            onClick={() => setView("gallery")}
            aria-pressed={view === "gallery"}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-md transition-all duration-300 font-medium ${
              view === "gallery"
                ? "bg-yellow-500 text-gray-900 shadow-lg"
                : "text-gray-300 hover:text-white hover:bg-gray-700/50"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
              />
            </svg>
            Gallery
          </button>
          <button
            type="button"
            onClick={() => setView("list")}
            aria-pressed={view === "list"}
            className={`inline-flex items-center gap-2 px-5 py-2 rounded-md transition-all duration-300 font-medium ${
              view === "list"
                ? "bg-yellow-500 text-gray-900 shadow-lg"
                : "text-gray-300 hover:text-white hover:bg-gray-700/50"
            }`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
            List
          </button>
        </div>
      </div>

      {view === "gallery" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {actingGigs.map((gig, index) => {
            const thumb = thumbnailFor(gig);
            return (
              <div
                key={index}
                className="group relative bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105 cursor-pointer active:scale-95"
                onClick={() => navigate(`/acting/${getGigUrl(gig.name)}`, { replace: false })}
              >
                <div className="relative h-48 bg-gray-900 overflow-hidden">
                  {thumb ? (
                    <img
                      src={thumb}
                      alt={gig.name}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const videoId = getVideoId(gig.youtube || "");
                        if (videoId && !videoId.startsWith("vimeo:")) {
                          target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-700 to-gray-800">
                      <svg className="w-16 h-16 text-gray-600" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                      </svg>
                    </div>
                  )}

                  {gig.youtube && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="w-16 h-16 rounded-full bg-yellow-500 flex items-center justify-center shadow-xl">
                        <svg className="w-8 h-8 text-gray-900 ml-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors">
                      {gig.name}
                    </h3>
                    {gig.year && (
                      <span className="text-sm text-gray-400 bg-gray-800 px-2 py-1 rounded">
                        {gig.year}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-sm font-medium text-yellow-500">{gig.category}</span>
                    {gig.role && (
                      <>
                        <span className="text-gray-500">•</span>
                        <span className="text-sm text-gray-400">{gig.role}</span>
                      </>
                    )}
                  </div>

                  {gig.description && (
                    <p className="text-sm text-gray-400 line-clamp-2 mb-3">{gig.description}</p>
                  )}

                  {gig.imdb && (
                    <div className="flex gap-2">
                      <a
                        href={gig.imdb}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs px-3 py-1 bg-yellow-500 text-gray-900 rounded-full hover:bg-yellow-400 transition-colors font-medium"
                      >
                        IMDb
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mb-12 divide-y divide-gray-800 border border-gray-800 rounded-xl overflow-hidden">
          {actingGigs.map((gig, index) => {
            const thumb = thumbnailFor(gig);
            return (
              <button
                key={index}
                type="button"
                onClick={() => navigate(`/acting/${getGigUrl(gig.name)}`, { replace: false })}
                className="w-full flex items-center gap-4 p-4 text-left bg-gray-900/40 hover:bg-gray-800/80 transition-colors group"
              >
                <div className="w-20 h-14 shrink-0 rounded-md overflow-hidden bg-gray-800">
                  {thumb ? (
                    <img src={thumb} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600">
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <h3 className="text-lg font-bold text-white group-hover:text-yellow-400 transition-colors truncate">
                      {gig.name}
                    </h3>
                    {gig.year && <span className="text-sm text-gray-500">{gig.year}</span>}
                  </div>
                  <p className="text-sm text-gray-400 truncate">
                    <span className="text-yellow-500">{gig.category}</span>
                    {gig.role ? ` · ${gig.role}` : ""}
                  </p>
                </div>

                <svg
                  className="w-5 h-5 text-gray-600 group-hover:text-yellow-500 shrink-0 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            );
          })}
        </div>
      )}
    </main>
  );
}

export default Gallery;
